import React, { useState, useEffect } from 'react'
import Plot from 'react-plotly.js'
import { computeSelectedWeekForSeason, getWeekFromWeek, getYearFromWeek, SeasonRange, seasonReferenceYear, toAbsoluteUrl } from '../../../../../../_metronic/helpers'
import axios from 'axios'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useSelectedWeek } from '../../../../../contexts/SelectedWeekContext'
import SourceLink from '../../component/chartLinkData'
// Sample data structure (modify as needed)
interface WeekData {
  week: string
  totalInternetUsers: number | null
  TotalMessages: number | null
}
type RespiratorySyndromesGraphProps = {
  season: SeasonRange,
  onClick: () => void
}
const RespiratorySyndromesGraph: React.FC<RespiratorySyndromesGraphProps> = ({ season, onClick }) => {
  const [data, setData] = useState<WeekData[]>([])
  const [missingData, setMissingData] = useState<WeekData[]>([])
  const { selectedWeek, setSelectedWeek } = useSelectedWeek('2025-W40')
  const [showArrow, setShowArrow] = useState<boolean>(false)
  const API_URL = process.env.REACT_APP_API_URL

  const [seasonSelectedWeek, setSeasonSelectedWeek] = useState<string | null>(null)
  const startYear = getYearFromWeek(season.startWeek)
  const endYear = getYearFromWeek(season.endWeek)
  const startWeekNumber = getWeekFromWeek(season.startWeek) + 1

  function addMissingWeek(data: WeekData[]): WeekData[] {
    const startWeek = season.startWeek // First week in the data
    const endWeek = season.endWeek // Last week in the data
    const existingWeeks = new Set(data.map((item) => item.week)) // Set to store existing weeks
    const newData: WeekData[] = []
    const missingData: WeekData[] = []
    let currentWeek = startWeek

    while (currentWeek !== endWeek) {
      // Move to the next week
      const [year, weekNum] = currentWeek.split('-W')
      const weekNumber = parseInt(weekNum)
      if (weekNumber < 52) {
        currentWeek = `${year}-W${(weekNumber + 1).toString()}`
      } else {
        currentWeek = `${parseInt(year) + 1}-W1`
      }
      if (!existingWeeks.has(currentWeek)) {
        newData.push({
          week: currentWeek,
          totalInternetUsers: null,
          TotalMessages: null,
        })
        missingData.push({
          week: currentWeek,
          totalInternetUsers: null,
          TotalMessages: null,
        })
      }
    }

    // Find the index to insert the new week
    let insertionIndex = 0
    for (let i = 0; i < data.length; i++) {
      if (data[i].week < `${startYear}-W52`) {
        insertionIndex = i + 1
      } else {
        break
      }
    }
    setMissingData(missingData)
    // Merge existing data with the newly added missing weeks
    const mergedData = [
      ...data.slice(0, insertionIndex),
      ...newData,
      ...data.slice(insertionIndex),
    ].sort((a, b) => {
      const aYear = parseInt(a.week.substring(0, 4), 10)
      const aWeek = parseInt(a.week.substring(a.week.indexOf('-W') + 2), 10)
      const bYear = parseInt(b.week.substring(0, 4), 10)
      const bWeek = parseInt(b.week.substring(b.week.indexOf('-W') + 2), 10)
      if (aYear !== bYear) {
        return aYear - bYear
      } else {
        return aWeek - bWeek
      }
    })

    return mergedData
  }
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API_URL}/number_users_respiratory_syndromes`)
        setData(
          addMissingWeek(response.data).filter((entry) => {
            const currentYear = parseInt(entry.week.substring(0, 4), 10)
            const currentWeek = parseInt(entry.week.substring(entry.week.indexOf('-W') + 2), 10)
            // return (currentYear === 2025 && currentWeek >= 36) || currentYear > 2025
            return (
              (currentYear === startYear && currentWeek >= startWeekNumber) ||
              (currentYear === endYear && currentWeek <= 27) ||
              (currentYear > startYear && currentYear < endYear)
            );
          })
        )

        let endWeek = season?.endWeek?.split('-W')
        const alignedWeek =
          startYear == seasonReferenceYear
            ? selectedWeek
            : computeSelectedWeekForSeason(selectedWeek, season.startWeek)
        const selectWeek = alignedWeek.split('-W')
        const showArrow =
          (Number(selectWeek[0]) > startYear ||
            (Number(selectWeek[0]) === startYear && Number(selectWeek[1]) >= startWeekNumber)) &&
          (Number(selectWeek[0]) < Number(endWeek[0]) ||
            (Number(selectWeek[0]) === Number(endWeek[0]) &&
              Number(selectWeek[1]) <= Number(endWeek[1])))
        setShowArrow(showArrow)
        setSeasonSelectedWeek(alignedWeek);

      } catch (error) {
        console.error('Error fetching data:', error)
        // Handle error or set a default value for data
        setData([])
      }
    }

    fetchData()
  }, [selectedWeek])

  return (
    <div>
      <div className='card px-4'>
        <div className='card-body row justify-content-md-center'>
          {
            startYear == seasonReferenceYear &&
            <h2 className='text-primary  p-2 bold'>
              Respiratory syndromes in social media (France){' '}
              <OverlayTrigger
                placement='right'
                overlay={
                  <Tooltip id='logo-tooltip' style={{ width: '300px' }}>
                    <p>
                      Based on extraction of messages featuring respiratory symptoms (extraction
                      performed by Brandwatch®), with 50 key medical concepts related to respiratory
                      symptoms (based on the MedDRA medical dictionary and enriched by social network
                      specific patient vocabulary). Data was modeled as a multidimensional time
                      series: each datapoint is composed of the number of posts and users containing
                      each concept on a given day. Data updated weekly. Source: KapCode
                    </p>
                  </Tooltip>
                }
              >
                <img
                  //className='mb-1'
                  style={{ width: '12px' }}
                  alt='logo'
                  src={toAbsoluteUrl('/media/logos/ii.svg')}
                />
              </OverlayTrigger>
            </h2>
          }

          {data.length > 0 ? (
            <Plot
              data={[
                {
                  type: 'scatter',
                  mode: 'lines',
                  name: 'Number of social media users reporting respiratory syndromes',
                  x: data.map((entry) => entry.week),
                  y: data.map((entry) => entry.totalInternetUsers),
                  marker: { color: '#203879' },
                  connectgaps: false,
                  hovertemplate:
                    '<b>%{fullData.name}</b><br>' +
                    'Week: %{x}<br>' +
                    'Number of social media users: %{y}<extra></extra>',
                },
                {
                  type: 'scatter',
                  mode: 'markers',
                  name: 'Missing Data',
                  x: missingData.map((entry) => entry.week),
                  y: missingData.map(() => 0),
                  marker: { color: '#000000' },
                  hovertemplate:
                    '<b>%{fullData.name}</b><br>' +
                    'Week: %{x}<br>' +
                    '%{fullData.name}: %{y}<extra></extra>',
                },
              ]}
              layout={{
                annotations: [
                  {
                    xref: 'paper',
                    yref: 'paper',
                    x: -0.035,
                    xanchor: 'right',
                    y: 0.5,
                    yanchor: 'bottom',
                    text: '#',
                    font: {
                      size: 25,

                      color: '#203879',
                    },
                    showarrow: false,
                  },
                  {
                    x: startYear == 2025
                      ? selectedWeek : seasonSelectedWeek,

                    xref: 'x',
                    yref: 'y',
                    text: showArrow ? 'Selected Week' : '',
                    font: {
                      family: 'Courier New, monospace',
                      size: 16,
                      color: '#CC1B1B',
                    },
                    arrowcolor: '#CC1B1B',
                    showarrow: showArrow,
                    arrowhead: 7,
                    ax: 0,
                    ay: -220,
                  },
                ],
                title: 'Number of social media users reporting respiratory syndromes',
                xaxis: { title: 'Week', tickangle: -45 },
                yaxis: {
                  // title: {
                  //   text: '#',
                  //   font: {
                  //     size: 25,
                  //     color: '#203879',
                  //   },
                  // },
                },
                legend: {
                  x: 0,
                  y: -0.4,
                  orientation: 'h',
                },
              }}
              config={{ displayModeBar: true, displaylogo: false, responsive: true }}
            />
          ) : (
            <p>Loading data...</p>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <p className='card-text source'>
              <i>
                Source:
                <a className='fw-bold' href='https://www.kapcode.fr/'>
                  {' '}
                  KapCode
                </a>
              </i>
            </p>
            {
              startYear == seasonReferenceYear &&
              <SourceLink onClick={onClick} />
            }
          </div>
        </div>
      </div>
    </div>
  )
}

export default RespiratorySyndromesGraph
